"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getChatMessages, type ChatMessage } from "@/lib/api";
import { useChatRealtime, type RealtimeStatus } from "@/hooks/useChatRealtime";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";
import { playChatNotifyBeep } from "@/lib/scanBeep";

const LAST_READ_KEY = "team_chat_last_read_msg_id";
// [ERP-TEAM-CHAT-GLOBAL-SOUND-NOTIFICATION-FIX-20260528-001]
// 알림음 설정 — TeamChatView 가 unmount 되어도 Provider 는 layout.tsx 에서 항상
// mount 이므로 채팅창 닫힌 상태/다른 페이지에서도 동작.
const SOUND_KEY = "team_chat_sound_enabled";
const ENTITY_TYPE = "team";
const ENTITY_ID = "general";

interface TeamChatContextValue {
  messages: ChatMessage[];
  lastMessageId: number;
  addMessage: (msg: ChatMessage) => void;
  replaceAll: (list: ChatMessage[]) => void;
  appendMany: (list: ChatMessage[]) => void;
  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  // 서버 최신 메시지로 기존 항목 교체(읽음수/삭제상태 반영) + 신규 추가.
  applyServerMessages: (list: ChatMessage[]) => void;
  unreadCount: number;
  currentUser: string;
  setCurrentUser: (name: string) => void;
  markAllRead: () => void;
  /** WS/폴링 연결 상태 — Provider 가 단일 드라이버를 소유하므로 전역에 공유 */
  realtimeStatus: RealtimeStatus;
  authed: boolean;
  refreshAuth: () => void;
  /** 26차 후속 #61 — 더 오래된 메시지가 서버에 더 있는지 */
  hasMoreOlder: boolean;
  /** 더 오래된 페이지를 prepend. 진행 중이면 noop. */
  loadOlder: () => Promise<void>;
  /** loadOlder in-flight */
  loadingOlder: boolean;
  /** [GLOBAL-SOUND-FIX] 알림음 설정 — Provider 가 전역에서 새 메시지 감지/재생 */
  soundEnabled: boolean;
  /** 알림음 토글 — 켜기 시 AudioContext unlock + 테스트 beep 1회 */
  toggleSound: () => void;
}

const TeamChatContext = createContext<TeamChatContextValue | null>(null);

function sortByCreatedThenId(list: ChatMessage[]): ChatMessage[] {
  // created_at 1차, id 2차 안정 정렬 — 같은 timestamp 메시지가 id 순으로 보존
  return [...list].sort((a, b) => {
    const ta = Date.parse(a.created_at);
    const tb = Date.parse(b.created_at);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    return a.id - b.id;
  });
}

function mergeById(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  // 순수 함수: prev 배열만으로 dedupe — setMessages updater 내부에서도 안전
  // (React 19/Next 16 StrictMode 또는 concurrent 렌더링이 updater 를 재실행해도 동일 결과)
  if (incoming.length === 0) return prev;
  const existingIds = new Set(prev.map((m) => m.id));
  const fresh = incoming.filter((m) => !existingIds.has(m.id));
  if (fresh.length === 0) return prev;
  return sortByCreatedThenId([...prev, ...fresh]);
}

// [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
// 서버 최근창 갱신용 — 기존 id 는 서버 최신값으로 "교체"(읽음수/삭제상태 반영), 신규 id 는 추가.
// mergeById(append-only) 와 달리 기존 항목을 갱신한다. 실제 변경(읽음수/삭제/본문) 이 없으면
// prev 참조를 그대로 반환해 불필요한 리렌더를 막는다.
function mergeReplaceById(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return prev;
  const prevMap = new Map(prev.map((m) => [m.id, m]));
  let changed = false;
  for (const m of incoming) {
    const p = prevMap.get(m.id);
    if (
      !p ||
      (p.read_count ?? 0) !== (m.read_count ?? 0) ||
      (p.unread_count ?? 0) !== (m.unread_count ?? 0) ||
      Boolean(p.is_deleted) !== Boolean(m.is_deleted) ||
      p.message !== m.message
    ) {
      changed = true;
      break;
    }
  }
  if (!changed) return prev;
  const incMap = new Map(incoming.map((m) => [m.id, m]));
  const prevIds = new Set(prev.map((m) => m.id));
  const merged = prev.map((m) => incMap.get(m.id) ?? m);
  const added = incoming.filter((m) => !prevIds.has(m.id));
  return sortByCreatedThenId(added.length ? [...merged, ...added] : merged);
}

function readLastReadId(): number {
  if (typeof window === "undefined") return 0;
  const raw = localStorage.getItem(LAST_READ_KEY);
  return raw ? parseInt(raw, 10) || 0 : 0;
}

function writeLastReadId(id: number): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(LAST_READ_KEY, String(id));
}

const INITIAL_PAGE_SIZE = 50;
const OLDER_PAGE_SIZE = 50;

export function TeamChatProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentUser, setCurrentUserState] = useState<string>("");
  const [lastReadId, setLastReadId] = useState<number>(0);
  const [authed, setAuthed] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [hasMoreOlder, setHasMoreOlder] = useState<boolean>(false);
  const [loadingOlder, setLoadingOlder] = useState<boolean>(false);

  // [GLOBAL-SOUND-FIX] 알림음 — TeamChatView 가 아닌 Provider 레벨에서 감지/재생.
  // 채팅 위젯 닫힘 / 다른 페이지 / TeamChatView unmount 상태에서도 동작.
  const [soundEnabled, setSoundEnabled] = useState<boolean>(false);
  const soundEnabledRef = useRef<boolean>(false);
  const currentUserRef = useRef<string>("");
  const knownMessageIdsRef = useRef<Set<number>>(new Set());
  const newestSeenIdRef = useRef<number>(0);
  const initialMessagesLoadedRef = useRef<boolean>(false);

  const setCurrentUser = useCallback((name: string) => {
    setCurrentUserState(name);
  }, []);

  const refreshAuth = useCallback(() => {
    if (typeof window === "undefined") return;
    const token = localStorage.getItem("access_token");
    const nextAuthed = Boolean(token);
    setAuthed(nextAuthed);
    if (nextAuthed) {
      setCurrentUserState((prev) =>
        prev || localStorage.getItem("user_name") || "직원",
      );
    } else {
      setCurrentUserState("");
      setMessages([]);
      setInitialized(false);
      setHasMoreOlder(false);
    }
  }, []);

  useEffect(() => {
    setLastReadId(readLastReadId());
    refreshAuth();
    function onStorage(e: StorageEvent) {
      if (
        e.key === "access_token" ||
        e.key === "user_name" ||
        e.key === LAST_READ_KEY ||
        e.key === null
      ) {
        refreshAuth();
        setLastReadId(readLastReadId());
      }
      // [GLOBAL-SOUND-FIX] 다른 탭에서 알림음 설정 변경 시 동기화
      if (e.key === SOUND_KEY || e.key === null) {
        if (typeof window !== "undefined") {
          const enabled = localStorage.getItem(SOUND_KEY) === "1";
          soundEnabledRef.current = enabled;
          setSoundEnabled(enabled);
        }
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshAuth]);

  // [GLOBAL-SOUND-FIX] localStorage 초기 로드 — Provider mount 시 1회.
  // soundEnabledRef 도 동기 갱신해 첫 message 도착보다 먼저 반영.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const enabled = localStorage.getItem(SOUND_KEY) === "1";
    soundEnabledRef.current = enabled;
    setSoundEnabled(enabled);
  }, []);

  // currentUser 최신값을 ref 로 mirror — 신규 메시지 effect 의 dep 최소화.
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      if (next) {
        // 사용자 제스처 컨텍스트에서 첫 호출 — AudioContext unlock + 테스트 beep 1회.
        try {
          playChatNotifyBeep();
        } catch {
          /* ignore — 일부 브라우저 정책 차단 시 */
        }
      }
      if (typeof window !== "undefined")
        localStorage.setItem(SOUND_KEY, next ? "1" : "0");
      // ref 즉시 동기 갱신 — mirror useEffect 한 렌더 지연 우회.
      soundEnabledRef.current = next;
      return next;
    });
  }, []);

  // replaceAll 도 merge 로 처리 — 초기 로드가 늦게 도착해도
  // 그 사이 WS/POST 로 들어온 local pending 메시지를 지우지 않음.
  const replaceAll = useCallback((list: ChatMessage[]) => {
    setMessages((prev) => mergeById(prev, list));
  }, []);

  const appendMany = useCallback((list: ChatMessage[]) => {
    setMessages((prev) => mergeById(prev, list));
  }, []);

  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  const applyServerMessages = useCallback((list: ChatMessage[]) => {
    setMessages((prev) => mergeReplaceById(prev, list));
  }, []);

  const addMessage = useCallback((msg: ChatMessage) => {
    setMessages((prev) => mergeById(prev, [msg]));
  }, []);

  // since_id 커서 — 정렬이 created_at 우선이라 배열 끝이 max id 와 다를 수 있어
  // 안전하게 모든 id 중 최대값 사용. (서버가 id > since_id 로 필터하므로 underflow 방지)
  const lastMessageId = useMemo(() => {
    let max = 0;
    for (const m of messages) {
      if (m.id > max) max = m.id;
    }
    return max;
  }, [messages]);

  // 인증 후 최초 1회 — 최신 INITIAL_PAGE_SIZE 건만 로드. (26차 후속 #61)
  useEffect(() => {
    if (!authed || initialized) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getChatMessages(ENTITY_TYPE, ENTITY_ID, {
          limit: INITIAL_PAGE_SIZE,
        });
        if (cancelled) return;
        replaceAll(res.messages);
        setHasMoreOlder(Boolean(res.has_more));
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setInitialized(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authed, initialized, replaceAll]);

  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  // 최근창 주기 갱신(10s) — 기존 메시지의 읽음수/삭제상태를 반영. REST 폴링(WS/SSE/Redis 신규 아님).
  // WS 는 신규 메시지 push 만 하므로, 기존 메시지 상태 변화(읽음 증가/삭제)는 이 폴링으로 동기화.
  // 이 GET 은 backend 에서 읽음 upsert 도 수행.
  // [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
  // 주기 10초 유지(안전정책: 채팅 열림시 30초 이하). hidden 탭 중지 + visible 복귀 즉시 1회 fetch
  // 는 useVisibilityPolling 이 담당(기존 document.hidden skip 을 승계·강화). authed=false 면 정지.
  const syncRecentMessages = useCallback(async () => {
    const res = await getChatMessages(ENTITY_TYPE, ENTITY_ID, { limit: 50 });
    applyServerMessages(res.messages);
  }, [applyServerMessages]);
  useVisibilityPolling(syncRecentMessages, {
    activeMs: 10000,
    enabled: authed,
    // 초기 fetch 는 위 "인증 후 최초 1회" effect 가 이미 수행 → 중복 방지 위해 immediate=false.
    immediate: false,
  });

  // 더 오래된 메시지 prepend. 메시지 배열의 oldest id 를 before_id 로 사용.
  const loadOlder = useCallback(async (): Promise<void> => {
    if (loadingOlder || !hasMoreOlder) return;
    let oldestId = 0;
    for (const m of messages) {
      if (oldestId === 0 || m.id < oldestId) oldestId = m.id;
    }
    if (oldestId <= 1) {
      // 더 이상 가져올 게 없는 경계 케이스 — 서버가 has_more=false 로 응답 보낼 때까지 한 번 더.
    }
    if (oldestId === 0) return;
    setLoadingOlder(true);
    try {
      const res = await getChatMessages(ENTITY_TYPE, ENTITY_ID, {
        before_id: oldestId,
        limit: OLDER_PAGE_SIZE,
      });
      // mergeById 가 dedupe + sort. 새 메시지(addMessage)와 충돌해도 안전.
      setMessages((prev) => mergeById(prev, res.messages));
      setHasMoreOlder(Boolean(res.has_more));
    } catch {
      /* ignore */
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasMoreOlder, messages]);

  const getSinceId = useCallback(() => lastMessageId, [lastMessageId]);

  // 단일 실시간 드라이버 — authed 바뀌면 훅 내부에서 재시작
  const { status: realtimeStatus } = useChatRealtime(
    ENTITY_TYPE,
    ENTITY_ID,
    {
      enabled: authed,
      onMessage: addMessage,
      onBatch: appendMany,
      getSinceId,
    },
  );

  // [GLOBAL-SOUND-FIX] 새 메시지 도착 감지 + 알림음 재생 — Provider 레벨 effect.
  //   known Set + newestSeenId 이중 가드 (직전 FIX 차수 로직 그대로, 위치만 이동).
  //   - 최초 messages 변화: 모든 id known 적재 + newestSeen=max → 사운드 없음
  //   - 이후 변화: known 에 없는 id 중 id > prevMaxId 만 trulyNew 후보
  //   - newestSeenId 는 monotone (loadOlder 가 끌어내리지 않음)
  //   - fromOthers (!is_system && sender_name !== me) 1건 이상이면 beep 1회
  //   - batch 라도 .some() 으로 beep 1회
  useEffect(() => {
    const valid = messages.filter((m) => typeof m.id === "number");
    let currentMaxId = 0;
    for (const m of valid) if (m.id > currentMaxId) currentMaxId = m.id;

    if (!initialMessagesLoadedRef.current) {
      for (const m of valid) knownMessageIdsRef.current.add(m.id);
      newestSeenIdRef.current = currentMaxId;
      initialMessagesLoadedRef.current = true;
      return;
    }

    const prevMaxId = newestSeenIdRef.current;
    const trulyNew: ChatMessage[] = [];
    for (const m of valid) {
      if (!knownMessageIdsRef.current.has(m.id)) {
        knownMessageIdsRef.current.add(m.id);
        if (m.id > prevMaxId) trulyNew.push(m);
      }
    }
    if (currentMaxId > newestSeenIdRef.current) {
      newestSeenIdRef.current = currentMaxId;
    }

    const me = currentUserRef.current;
    const fromOthers = trulyNew.some(
      (m) => !m.is_system && m.sender_name !== me,
    );
    if (soundEnabledRef.current && fromOthers) {
      try {
        playChatNotifyBeep();
      } catch {
        /* ignore */
      }
    }
  }, [messages]);

  const unreadCount = useMemo(() => {
    if (!currentUser) return 0;
    return messages.reduce((acc, m) => {
      if (m.id <= lastReadId) return acc;
      if (m.is_system) return acc;
      if (m.sender_name === currentUser) return acc;
      return acc + 1;
    }, 0);
  }, [messages, lastReadId, currentUser]);

  const markAllRead = useCallback(() => {
    setLastReadId((prev) => {
      // lastMessageId 는 max(id) — created_at 정렬 우선이라 배열 끝이 max 가 아닐 수 있음
      const newest = lastMessageId || prev;
      if (newest <= prev) return prev;
      writeLastReadId(newest);
      return newest;
    });
  }, [lastMessageId]);

  const value = useMemo<TeamChatContextValue>(
    () => ({
      messages,
      lastMessageId,
      addMessage,
      replaceAll,
      appendMany,
      applyServerMessages,
      unreadCount,
      currentUser,
      setCurrentUser,
      markAllRead,
      realtimeStatus,
      authed,
      refreshAuth,
      hasMoreOlder,
      loadOlder,
      loadingOlder,
      soundEnabled,
      toggleSound,
    }),
    [
      messages,
      lastMessageId,
      addMessage,
      replaceAll,
      appendMany,
      applyServerMessages,
      unreadCount,
      currentUser,
      setCurrentUser,
      markAllRead,
      realtimeStatus,
      authed,
      refreshAuth,
      hasMoreOlder,
      loadOlder,
      loadingOlder,
      soundEnabled,
      toggleSound,
    ],
  );

  return (
    <TeamChatContext.Provider value={value}>{children}</TeamChatContext.Provider>
  );
}

export function useTeamChat(): TeamChatContextValue {
  const ctx = useContext(TeamChatContext);
  if (!ctx) {
    throw new Error("useTeamChat must be used within <TeamChatProvider>");
  }
  return ctx;
}
